import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, HelpCircle, Search } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import {
  addDays,
  addMonths,
  birthdaysOnDay,
  expandEvents,
  filterByMember,
  formatTime,
  groupOccurrencesByDay,
  isSameDay,
  monthGridDays,
  monthName,
  shortDate,
  startOfDay,
  toDateKey,
  upcomingBirthdays,
  weekDays,
  type CalendarEvent,
  type EventOccurrence,
} from '@awwyeah/shared';
import { useUser } from '../auth';
import { addTargetDay } from './addTarget';
import { CalendarLegend } from './components/CalendarLegend';
import { DayAgenda } from './components/DayAgenda';
import { Fab } from '../../components/Fab';
import { EventDetailSheet } from './components/EventDetailSheet';
import {
  draftToTimes,
  EventFormSheet,
  type EventDraft,
  type SaveScope,
} from './components/EventFormSheet';
import { MemberFilterChips } from './components/MemberBits';
import { MonthView } from './components/MonthView';
import { styleForOwners, type MemberView } from './members';
import { SearchOverlay } from './components/SearchOverlay';
import { UndoToast } from './components/UndoToast';
import { UpcomingBirthdays } from './components/UpcomingBirthdays';
import { WeekView } from './components/WeekView';
import { useCalendarStore, useHouseholdData, useNewEventPlacement } from './useCalendarData';
import { useWeekStart } from './weekStartPreference';

type ViewMode = 'month' | 'week' | 'day';

const VIEWS: { id: ViewMode; label: string }[] = [
  { id: 'month', label: 'Month' },
  { id: 'week', label: 'Week' },
  { id: 'day', label: 'Day' },
];

/**
 * Parses the `?d=` deep-link parameter into a local start-of-day, or `null` for anything
 * that isn't a real date. Constructed field-by-field rather than `new Date(key)`, which
 * parses a bare `YYYY-MM-DD` as **UTC** and would land the day before local midnight for
 * everyone west of Greenwich — the whole household, in this case.
 */
function parseDayParam(raw: string | null): Date | null {
  if (!raw) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!match) return null;
  const [, year, month, day] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(parsed.getTime())) return null;
  // Rejects the likes of `2026-02-31`, which `Date` would silently roll into March.
  if (parsed.getMonth() !== Number(month) - 1 || parsed.getDate() !== Number(day)) return null;
  return startOfDay(parsed);
}

interface FormState {
  open: boolean;
  event: CalendarEvent | null;
  occurrenceStart?: string;
}

interface UndoState {
  message: string;
  run: () => void;
}

/**
 * The calendar route. Month / week / day of the same data, a member filter, instant
 * search and the create-edit sheet — all reading one Yjs doc, all working offline
 * (design Screen C; ROADMAP §2 Phase 1).
 */
export function CalendarPage(): JSX.Element {
  const { weekStartsOn } = useWeekStart();
  const { user } = useUser();
  const store = useCalendarStore();
  const household = useHouseholdData();
  const { placement: newEventPlacement } = useNewEventPlacement();
  const [searchParams] = useSearchParams();

  // `?d=YYYY-MM-DD` — "open the calendar on this day", used by the digest's month card
  // (`CalendarSection`). Arriving with a day means you already picked one, so the page
  // opens in day view rather than dropping you in the month grid to pick it again.
  const requestedDayKey = searchParams.get('d');
  const requestedDay = useMemo(() => parseDayParam(requestedDayKey), [requestedDayKey]);

  const [now, setNow] = useState(() => new Date());
  const [view, setView] = useState<ViewMode>(requestedDay ? 'day' : 'month');
  const [cursor, setCursor] = useState(() => requestedDay ?? startOfDay(new Date()));
  const [selectedDay, setSelectedDay] = useState(() => requestedDay ?? startOfDay(new Date()));
  const [memberFilter, setMemberFilter] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [detail, setDetail] = useState<EventOccurrence | null>(null);
  const [form, setForm] = useState<FormState>({ open: false, event: null });
  const [undo, setUndo] = useState<UndoState | null>(null);
  const [legendOpen, setLegendOpen] = useState(false);

  // The "now" rule and today's frame stay honest without a re-render per second.
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);

  // A second `?d=` while the page is already mounted (tapping another day in the digest
  // card without a full remount) has to move the view too — the `useState` initialisers
  // above only ever run once. Keyed on the raw param so re-renders don't fight the user's
  // own later navigation within the page.
  useEffect(() => {
    if (!requestedDay) return;
    setSelectedDay(requestedDay);
    setCursor(requestedDay);
    setView('day');
  }, [requestedDay]);

  const window = useMemo(() => {
    if (view === 'month') {
      const cells = monthGridDays(cursor, weekStartsOn);
      const first = cells[0] ?? cursor;
      const last = cells[cells.length - 1] ?? cursor;
      return { from: first.getTime(), to: addDays(last, 1).getTime() };
    }
    if (view === 'week') {
      const days = weekDays(cursor, weekStartsOn);
      const first = days[0] ?? cursor;
      // Three weeks: the carousel renders the previous and next weeks either side of the
      // one on screen, and they must be populated *before* the swipe reveals them.
      return { from: addDays(first, -7).getTime(), to: addDays(first, 14).getTime() };
    }
    return {
      from: startOfDay(selectedDay).getTime(),
      to: addDays(startOfDay(selectedDay), 1).getTime(),
    };
  }, [view, cursor, selectedDay, weekStartsOn]);

  const occurrences = useMemo(
    () => filterByMember(expandEvents(store.events, window), memberFilter),
    [store.events, window, memberFilter],
  );
  const byDay = useMemo(() => groupOccurrencesByDay(occurrences), [occurrences]);

  // The selected day's agenda is needed in month view too (the bottom strip), and it may
  // sit outside the grid window when the day view is showing a different date.
  const selectedDayOccurrences = useMemo(() => {
    const from = startOfDay(selectedDay).getTime();
    const dayWindow = { from, to: addDays(startOfDay(selectedDay), 1).getTime() };
    return filterByMember(expandEvents(store.events, dayWindow), memberFilter);
  }, [store.events, selectedDay, memberFilter]);

  const birthdaySoon = useMemo(
    () => upcomingBirthdays(household.birthdays, { now, withinDays: 120, limit: 4 }),
    [household.birthdays, now],
  );
  const birthdayDays = useMemo(
    () =>
      new Set(
        upcomingBirthdays(household.birthdays, { now, withinDays: 400 }).map((entry) => entry.date),
      ),
    [household.birthdays, now],
  );
  const titleSuggestions = useMemo(
    () => [...new Set(store.events.map((event) => event.title))].sort(),
    [store.events],
  );

  const shift = useCallback(
    (direction: 1 | -1) => {
      if (view === 'month') setCursor((current) => addMonths(current, direction));
      else if (view === 'week') setCursor((current) => addDays(current, 7 * direction));
      else {
        setSelectedDay((current) => addDays(current, direction));
        setCursor((current) => addDays(current, direction));
      }
    },
    [view],
  );

  const goToday = useCallback(() => {
    const today = startOfDay(new Date());
    setCursor(today);
    setSelectedDay(today);
  }, []);

  const openDay = useCallback((day: Date) => {
    setSelectedDay(startOfDay(day));
    setCursor(startOfDay(day));
    setView('day');
  }, []);

  const selectDay = useCallback(
    (day: Date) => {
      if (isSameDay(day, selectedDay)) openDay(day);
      else setSelectedDay(startOfDay(day));
    },
    [openDay, selectedDay],
  );

  // The add button always means "add something on the day I'm looking at".
  const addDay = useMemo(
    () => addTargetDay({ view, cursor, selectedDay, today: now, weekStartsOn }),
    [view, cursor, selectedDay, now, weekStartsOn],
  );

  const openNew = useCallback(() => setForm({ open: true, event: null }), []);

  const openEdit = useCallback((occurrence: EventOccurrence) => {
    setDetail(null);
    setForm({ open: true, event: occurrence.event, occurrenceStart: occurrence.start });
  }, []);

  const handleSave = useCallback(
    (draft: EventDraft, scope: SaveScope) => {
      const { start, end } = draftToTimes(draft);
      const patch = {
        title: draft.title,
        start,
        end,
        allDay: draft.allDay,
        ownerIds: draft.ownerIds,
        location: draft.location.trim() || undefined,
        notes: draft.notes.trim() || undefined,
        reminders: draft.reminders.slice(0, 3),
        recurrence: draft.recurrence,
        // Soft, per-event subset filter. Only meaningful in the shared doc — a personal
        // event is already invisible to everyone else, and carrying a stale list into it
        // would be a second, weaker claim about the same event.
        audience: draft.audience === 'members' ? draft.audienceIds : undefined,
      };
      const placement = draft.audience === 'private' ? 'personal' : 'family';
      const target = form.event;
      if (!target) {
        store.createEvent({ ...patch, exdates: [], source: { kind: 'local' } }, placement);
      } else if (scope === 'this' && target.recurrence && form.occurrenceStart) {
        // A detached occurrence inherits its series' doc; moving one occurrence of a
        // repeating event between docs is not a thing the model supports, so "Just me" on
        // "just this one" applies to the series' existing home.
        store.detachOccurrence(target.id, form.occurrenceStart, patch);
      } else {
        store.updateEvent(target.id, patch);
        // Order matters: patch first (so the moved copy carries the edit), then move.
        if (store.placementOf(target.id) !== placement) store.setPlacement(target.id, placement);
      }
      setForm({ open: false, event: null });
    },
    [form, store],
  );

  const handleDelete = useCallback(
    (occurrence: EventOccurrence, scope: SaveScope) => {
      setDetail(null);
      if (scope === 'this' && occurrence.event.recurrence) {
        const start = occurrence.start;
        const id = occurrence.event.id;
        store.excludeOccurrence(id, start);
        setUndo({
          message: `Deleted one ${occurrence.event.title}`,
          run: () => {
            const parent = store.byId.get(id);
            if (parent) {
              store.updateEvent(id, {
                exdates: parent.exdates.filter((exdate) => exdate !== start),
              });
            }
            setUndo(null);
          },
        });
        return;
      }
      const removed = store.deleteEvent(occurrence.event.id);
      if (!removed) return;
      setUndo({
        message: `Deleted ${removed.title}`,
        run: () => {
          store.restoreEvent(removed);
          setUndo(null);
        },
      });
    },
    [store],
  );

  const touchStart = useRef<number | null>(null);
  const onTouchStart = (event: React.TouchEvent): void => {
    touchStart.current = event.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (event: React.TouchEvent): void => {
    const start = touchStart.current;
    const end = event.changedTouches[0]?.clientX;
    touchStart.current = null;
    if (start === null || end === undefined) return;
    // Horizontal swipe moves through time, never through views (design flow notes).
    if (Math.abs(end - start) > 56) shift(end < start ? 1 : -1);
  };

  const period =
    view === 'month'
      ? { main: monthName(cursor), sub: `${cursor.getFullYear()}` }
      : view === 'week'
        ? {
            main: shortDate(weekDays(cursor, weekStartsOn)[0] ?? cursor, now),
            sub: `– ${shortDate(weekDays(cursor, weekStartsOn)[6] ?? cursor, now)}`,
          }
        : { main: monthName(selectedDay), sub: `${selectedDay.getFullYear()}` };

  return (
    <div className="flex flex-col gap-1 pb-36" data-testid="calendar-page">
      <header className="flex items-center justify-between gap-2 pt-2">
        <div className="flex items-baseline gap-2">
          <h1 className="font-display text-[22px] tracking-tight">{period.main}</h1>
          <span className="text-sm text-fg-3">{period.sub}</span>
        </div>
        <div className="flex items-center gap-1">
          <IconButton label="Previous" onClick={() => shift(-1)} testId="calendar-prev">
            <ChevronLeft className="h-5 w-5" />
          </IconButton>
          <button
            type="button"
            onClick={goToday}
            data-testid="calendar-today-button"
            className="h-11 rounded-full border border-line px-3 text-[13px] font-semibold text-fg-2"
          >
            Today
          </button>
          <IconButton label="Next" onClick={() => shift(1)} testId="calendar-next">
            <ChevronRight className="h-5 w-5" />
          </IconButton>
          <IconButton
            label="Search events"
            onClick={() => setSearchOpen(true)}
            testId="calendar-search-button"
          >
            <Search className="h-5 w-5" />
          </IconButton>
        </div>
      </header>

      <div className="flex items-stretch gap-2">
        <div className="grid flex-1 grid-cols-3 rounded-sm border border-line" role="tablist">
          {VIEWS.map((option) => (
            <button
              key={option.id}
              type="button"
              role="tab"
              aria-selected={view === option.id}
              data-testid={`calendar-view-${option.id}`}
              onClick={() => setView(option.id)}
              className={`h-11 text-[13px] font-semibold transition-colors duration-150 ease-app ${
                view === option.id ? 'bg-inverse text-inverse-fg' : 'text-fg-2'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        {/* "What do these marks mean?" — testers could not decode the dots, 2026-08-01. */}
        <button
          type="button"
          onClick={() => setLegendOpen((open) => !open)}
          aria-expanded={legendOpen}
          aria-label="What the marks mean"
          data-testid="calendar-legend-toggle"
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-sm border border-line transition-colors duration-150 ease-app ${
            legendOpen ? 'bg-inverse text-inverse-fg' : 'text-fg-2'
          }`}
        >
          <HelpCircle className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      {legendOpen && (
        <CalendarLegend members={household.members} onClose={() => setLegendOpen(false)} />
      )}

      <MemberFilterChips
        members={household.members}
        selected={memberFilter}
        onSelect={setMemberFilter}
      />

      {/* Week view brings its own horizontal carousel, so the swipe-to-shift shim would
          fight it — month and day keep it. */}
      <div {...(view === 'week' ? {} : { onTouchStart, onTouchEnd })}>
        {view === 'month' && (
          <MonthView
            month={cursor}
            today={now}
            selectedDay={selectedDay}
            byDay={byDay}
            birthdayDays={birthdayDays}
            members={household.byId}
            onSelectDay={selectDay}
          />
        )}
        {view === 'week' && (
          <WeekView
            anchor={cursor}
            today={now}
            now={now}
            byDay={byDay}
            members={household.byId}
            onSelectDay={openDay}
            onOpenOccurrence={setDetail}
            onNavigateWeek={shift}
          />
        )}
        {view === 'day' && (
          <DayAgenda
            day={selectedDay}
            now={now}
            occurrences={selectedDayOccurrences}
            birthdays={birthdaysOnDay(household.birthdays, toDateKey(selectedDay))}
            members={household.byId}
            currentUserId={user?.id ?? null}
            onOpenOccurrence={setDetail}
            onAdd={openNew}
          />
        )}
      </div>

      {view !== 'day' && (
        <section className="border-t border-line pt-3" data-testid="day-strip">
          <button
            type="button"
            onClick={() => openDay(selectedDay)}
            data-testid="day-strip-header"
            className="flex w-full items-baseline justify-between pb-2"
          >
            <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-fg-3">
              {shortDate(selectedDay, now)}
              {isSameDay(selectedDay, now) ? ' · today' : ''}
            </span>
            <span className="text-[12px] text-fg-3">
              {selectedDayOccurrences.length === 0
                ? 'nothing'
                : `${selectedDayOccurrences.length} thing${selectedDayOccurrences.length === 1 ? '' : 's'} →`}
            </span>
          </button>
          <DayStrip
            occurrences={selectedDayOccurrences}
            members={household.byId}
            onOpen={setDetail}
            onOpenDay={() => openDay(selectedDay)}
          />
        </section>
      )}

      <UpcomingBirthdays entries={birthdaySoon} members={household.byId} now={now} />

      <Fab
        label={`Add event on ${shortDate(addDay, now)}`}
        onClick={openNew}
        testId="event-add-button"
      />

      <EventDetailSheet
        occurrence={detail}
        members={household.byId}
        now={now}
        onClose={() => setDetail(null)}
        onEdit={openEdit}
        onDelete={handleDelete}
        personal={detail ? store.placementOf(detail.event.id) === 'personal' : false}
      />

      <EventFormSheet
        open={form.open}
        event={form.event}
        {...(form.occurrenceStart ? { occurrenceStart: form.occurrenceStart } : {})}
        defaultDay={addDay}
        defaultOwnerIds={user ? [user.id] : []}
        members={household.members}
        titleSuggestions={titleSuggestions}
        placement={form.event ? store.placementOf(form.event.id) : 'family'}
        newEventPlacement={newEventPlacement}
        meId={user?.id ?? null}
        onCancel={() => setForm({ open: false, event: null })}
        onSave={handleSave}
      />

      {searchOpen && (
        <SearchOverlay
          events={store.events}
          members={household.byId}
          now={now}
          onClose={() => setSearchOpen(false)}
          onOpenOccurrence={(occurrence) => {
            setSearchOpen(false);
            setDetail(occurrence);
          }}
        />
      )}

      {undo && (
        <UndoToast message={undo.message} onUndo={undo.run} onDismiss={() => setUndo(null)} />
      )}
    </div>
  );
}

function DayStrip({
  occurrences,
  members,
  onOpen,
  onOpenDay,
}: {
  occurrences: readonly EventOccurrence[];
  members: ReadonlyMap<string, MemberView>;
  onOpen: (occurrence: EventOccurrence) => void;
  onOpenDay: () => void;
}): JSX.Element {
  if (occurrences.length === 0) {
    return (
      <p className="pb-2 text-sm text-fg-3">
        Wide open. Adding works fine offline — it&apos;ll sync when you&apos;re back.
      </p>
    );
  }
  const shown = occurrences.slice(0, 2);
  return (
    <div className="flex flex-col gap-2">
      {shown.map((occurrence) => (
        <StripRow key={occurrence.key} occurrence={occurrence} members={members} onOpen={onOpen} />
      ))}
      {occurrences.length > shown.length && (
        <button
          type="button"
          onClick={onOpenDay}
          className="self-start py-1 text-[13px] font-semibold text-accent"
        >
          {occurrences.length - shown.length} more →
        </button>
      )}
    </div>
  );
}

function StripRow({
  occurrence,
  members,
  onOpen,
}: {
  occurrence: EventOccurrence;
  members: ReadonlyMap<string, MemberView>;
  onOpen: (occurrence: EventOccurrence) => void;
}): JSX.Element {
  const style = styleForOwners(occurrence.event.ownerIds, members);
  return (
    <button
      type="button"
      data-testid="event-item"
      data-event-id={occurrence.event.id}
      onClick={() => onOpen(occurrence)}
      className="flex min-h-[44px] w-full items-center gap-2.5 text-left"
    >
      <span className="h-8 w-1 shrink-0 rounded-sm" style={style.dot} />
      <span className="w-12 shrink-0 text-[13px] font-semibold">
        {occurrence.event.allDay ? 'all' : formatTime(occurrence.start)}
      </span>
      <span className="min-w-0 flex-1 truncate text-[15px]">{occurrence.event.title}</span>
    </button>
  );
}

function IconButton({
  label,
  onClick,
  testId,
  children,
}: {
  label: string;
  onClick: () => void;
  testId: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      data-testid={testId}
      onClick={onClick}
      className="flex h-11 w-11 items-center justify-center text-fg-2"
    >
      {children}
    </button>
  );
}
